import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  IconButton,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { withTranslation } from 'react-i18next';
import { format } from 'date-fns';
import axios from 'axios';
import MaintenanceBlockSelect from './MaintenanceBlockSelect';
import { formatQuantity } from '../../utils';

// Helper to convert "HH:mm" string to Date object
const timeStringToDate = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

// Helper to convert Date object to "HH:mm" string
const dateToTimeString = (date) => {
  if (!date) return '';
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Helper to convert "yyyy-MM-dd" string to Date object
const dateStringToDateObj = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr + 'T00:00:00');
};

// Helper to convert Date object to "yyyy-MM-dd" string
const dateObjToDateString = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  return format(date, 'yyyy-MM-dd');
};

const WorkDayDialog = ({
  t,
  open,
  onClose,
  workDay,
  onSaved,
  employees,
  isManagerRole,
  selectedEmployee,
  selectedDate,
}) => {
  const isEditing = Boolean(workDay?.id);

  // Form state
  const [formData, setFormData] = useState({
    employee: '',
    work_date: format(new Date(), 'yyyy-MM-dd'),
    blocks: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [overlapWarnings, setOverlapWarnings] = useState([]);
  const [shiftTimes, setShiftTimes] = useState({ start: '08:00', end: '16:00' });
  const [lunchTime, setLunchTime] = useState(null);
  const errorRef = React.useRef(null);

  // The dialog can be long (a clock-tracked day has many blocks); an error rendered at the top
  // is off-screen when the user is scrolled down — bring it into view so Save never fails silently.
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Calculate total hours
  const calculateTotalHours = useCallback((blocks) => {
    return blocks.reduce((sum, block) => {
      if (!block.start_time || !block.end_time) return sum;
      const [startH, startM] = block.start_time.split(':').map(Number);
      const [endH, endM] = block.end_time.split(':').map(Number);
      let hours = endH - startH + (endM - startM) / 60;
      // Handle overnight
      if (hours < 0) hours += 24;
      return sum + hours;
    }, 0);
  }, []);

  // Check for overlapping time blocks
  const checkOverlaps = useCallback((blocks) => {
    const warnings = [];
    const timeToMinutes = (timeStr) => {
      if (!timeStr) return 0;
      const [h, m] = timeStr.split(':').map(Number);
      return h * 60 + m;
    };

    for (let i = 0; i < blocks.length; i++) {
      for (let j = i + 1; j < blocks.length; j++) {
        const block1Start = timeToMinutes(blocks[i].start_time);
        const block1End = timeToMinutes(blocks[i].end_time);
        const block2Start = timeToMinutes(blocks[j].start_time);
        const block2End = timeToMinutes(blocks[j].end_time);

        // Check if time ranges overlap
        if (block1Start < block2End && block1End > block2Start) {
          warnings.push(
            t('Block {{block1}} overlaps with Block {{block2}}', { block1: i + 1, block2: j + 1 })
          );
        }
      }
    }
    return warnings;
  }, [t]);

  // Update overlap warnings when blocks change
  useEffect(() => {
    if (formData.blocks.length > 1) {
      setOverlapWarnings(checkOverlaps(formData.blocks));
    } else {
      setOverlapWarnings([]);
    }
  }, [formData.blocks, checkOverlaps]);

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      setError(null);
      setOverlapWarnings([]);

      if (workDay && workDay.id) {
        // Editing existing work day
        setFormData({
          employee: workDay.employee,
          work_date: workDay.work_date,
          blocks: workDay.blocks.map((block) => ({
            id: block.id,
            start_time: block.start_time.substring(0, 5),
            end_time: block.end_time.substring(0, 5),
            activity_type: block.activity_type,
            maintenance: block.maintenance,
            maintenance_info: block.maintenance_info || null,
            other_description: block.other_description || '',
            notes: block.notes || '',
          })),
        });
      } else {
        // New work day - fetch existing or get template
        const date = selectedDate || format(new Date(), 'yyyy-MM-dd');
        const employee = selectedEmployee || '';
        setFormData({
          employee: employee,
          work_date: date,
          blocks: [],
        });

        // Fetch by_date to check if day exists
        // For non-managers (no employee selected), the backend auto-detects the employee
        fetchWorkDay(date, employee || null);
      }
    }
  }, [open, workDay, selectedEmployee, selectedDate]);

  // Fetch work day data
  const fetchWorkDay = async (date, employeeId) => {
    setLoading(true);
    try {
      // Build params - only include employee if explicitly set (for managers)
      const params = { date };
      if (employeeId) {
        params.employee = employeeId;
      }

      const response = await axios.get('/api/scheduling/work-days/by_date/', { params });

      if (response.data.exists) {
        const wd = response.data.work_day;
        setFormData((prev) => ({
          ...prev,
          employee: wd.employee,
          work_date: wd.work_date,
          blocks: wd.blocks.map((block) => ({
            id: block.id,
            start_time: block.start_time.substring(0, 5),
            end_time: block.end_time.substring(0, 5),
            activity_type: block.activity_type,
            maintenance: block.maintenance,
            maintenance_info: block.maintenance_info || null,
            other_description: block.other_description || '',
            notes: block.notes || '',
          })),
        }));
      } else {
        // Update employee from response if provided (for non-managers)
        if (response.data.employee) {
          setFormData((prev) => ({ ...prev, employee: response.data.employee }));
        }
        // Try to get prefill suggestions
        fetchPrefill(date, employeeId);
      }
    } catch (err) {
      console.error('Error fetching work day:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch prefill suggestions
  const fetchPrefill = async (date, employeeId) => {
    try {
      // Build params - only include employee if explicitly set (for managers)
      const params = { date };
      if (employeeId) {
        params.employee = employeeId;
      }

      const response = await axios.get('/api/scheduling/work-days/prefill/', { params });

      // Save shift times for use when adding new blocks
      if (response.data.shift_start || response.data.shift_end) {
        setShiftTimes({
          start: response.data.shift_start || '08:00',
          end: response.data.shift_end || '16:00',
        });
      }

      if (response.data.blocks && response.data.blocks.length > 0) {
        setFormData((prev) => ({
          ...prev,
          blocks: response.data.blocks.map((block) => ({
            id: null,
            start_time: block.start_time,
            end_time: block.end_time,
            activity_type: block.activity_type,
            maintenance: block.maintenance,
            other_description: block.other_description || '',
            notes: block.notes || '',
          })),
        }));
      }
    } catch (err) {
      console.error('Error fetching prefill:', err);
    }
  };

  // Handle date change
  const handleDateChange = (newDate) => {
    setFormData((prev) => ({ ...prev, work_date: newDate, blocks: [] }));
    // Always fetch - backend auto-detects employee for non-managers
    fetchWorkDay(newDate, formData.employee || null);
  };

  // Handle employee change
  const handleEmployeeChange = (newEmployee) => {
    setFormData((prev) => ({ ...prev, employee: newEmployee, blocks: [] }));
    if (newEmployee) {
      fetchWorkDay(formData.work_date, newEmployee);
    }
  };

  // Add a new block with smart defaults
  const addBlock = () => {
    // Calculate smart start/end times based on existing blocks
    let newStartTime = shiftTimes.start;
    let newEndTime = shiftTimes.end;

    if (formData.blocks.length > 0) {
      // Find the latest end_time among existing blocks
      const latestEndTime = formData.blocks.reduce((latest, block) => {
        if (!block.end_time) return latest;
        if (!latest) return block.end_time;
        // Compare times as strings (HH:MM format works for comparison)
        return block.end_time > latest ? block.end_time : latest;
      }, null);

      if (latestEndTime) {
        newStartTime = latestEndTime;
        // If last block already ends at or after shift end, leave end empty
        if (latestEndTime >= shiftTimes.end) {
          newEndTime = '';
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      blocks: [
        ...prev.blocks,
        {
          id: null,
          start_time: newStartTime,
          end_time: newEndTime,
          activity_type: 'maintenance',
          maintenance: null,
          other_description: '',
          notes: '',
        },
      ],
    }));
  };

  // Update a block
  const updateBlock = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      blocks: prev.blocks.map((block, i) =>
        i === index ? { ...block, [field]: value } : block
      ),
    }));
  };

  // Remove a block
  const removeBlock = (index) => {
    setFormData((prev) => ({
      ...prev,
      blocks: prev.blocks.filter((_, i) => i !== index),
    }));
  };

  // Calculate center of work day for lunch break default
  const calculateWorkDayCenter = useCallback((blocks) => {
    if (blocks.length === 0) return null;
    const timeToMinutes = (timeStr) => {
      if (!timeStr) return 0;
      const [h, m] = timeStr.split(':').map(Number);
      return h * 60 + m;
    };
    const minutesToTime = (mins) => {
      const h = Math.floor(mins / 60).toString().padStart(2, '0');
      const m = (mins % 60).toString().padStart(2, '0');
      return `${h}:${m}`;
    };

    let earliest = Infinity;
    let latest = -Infinity;
    blocks.forEach((block) => {
      if (block.start_time) earliest = Math.min(earliest, timeToMinutes(block.start_time));
      if (block.end_time) latest = Math.max(latest, timeToMinutes(block.end_time));
    });

    if (earliest === Infinity || latest === -Infinity) return null;
    const center = Math.round((earliest + latest) / 2);
    return minutesToTime(center);
  }, []);

  // Update lunch break default time when blocks change
  useEffect(() => {
    if (formData.blocks.length > 0 && !lunchTime) {
      const center = calculateWorkDayCenter(formData.blocks);
      if (center) setLunchTime(timeStringToDate(center));
    }
  }, [formData.blocks, lunchTime, calculateWorkDayCenter]);

  // Quick-add a meal break (Ruokatauko) as an explicit break block at the chosen time. If it
  // falls inside a work block, split that block (work / break / work) so the day stays contiguous;
  // otherwise just add the break block on its own. (Previously this left a bare GAP — the legacy
  // "break = hole" behaviour — which ignored the org's paid-break setting and didn't show as a
  // break in the timeline.)
  const handleLunchBreak = (durationMinutes) => {
    if (!lunchTime) return;

    const timeToMinutes = (timeStr) => {
      if (!timeStr) return 0;
      const [h, m] = timeStr.split(':').map(Number);
      return h * 60 + m;
    };
    const minutesToTime = (mins) => {
      const h = Math.floor(mins / 60).toString().padStart(2, '0');
      const m = (mins % 60).toString().padStart(2, '0');
      return `${h}:${m}`;
    };

    const lunchMins = timeToMinutes(dateToTimeString(lunchTime));
    const breakStart = minutesToTime(lunchMins);
    const breakEnd = minutesToTime(lunchMins + durationMinutes);

    const breakBlock = {
      id: null,
      start_time: breakStart,
      end_time: breakEnd,
      activity_type: 'break',
      maintenance: null,
      other_description: '',
      notes: '',
    };

    // Find a work block that fully contains the break → split it; else add standalone.
    const blockIndex = formData.blocks.findIndex((block) => {
      if (!block.start_time || !block.end_time) return false;
      return timeToMinutes(block.start_time) <= lunchMins && timeToMinutes(block.end_time) >= lunchMins + durationMinutes;
    });

    let newBlocks;
    if (blockIndex !== -1) {
      const originalBlock = formData.blocks[blockIndex];
      const hasBefore = timeToMinutes(breakStart) > timeToMinutes(originalBlock.start_time);
      const hasAfter = timeToMinutes(breakEnd) < timeToMinutes(originalBlock.end_time);
      const parts = [];
      // Keep the original id on the first surviving work part (update in place); the rest are new.
      if (hasBefore) parts.push({ ...originalBlock, end_time: breakStart });
      parts.push(breakBlock);
      if (hasAfter) parts.push({ ...originalBlock, id: hasBefore ? null : originalBlock.id, start_time: breakEnd });
      newBlocks = [...formData.blocks];
      newBlocks.splice(blockIndex, 1, ...parts);
    } else {
      newBlocks = [...formData.blocks, breakBlock];
    }

    setFormData((prev) => ({ ...prev, blocks: newBlocks }));
  };

  // The break can be added when it fits cleanly: either fully inside a work block (→ split it),
  // or not overlapping any block at all (→ standalone). A partial overlap is blocked to avoid
  // creating overlapping time.
  const canInsertLunchBreak = useCallback((durationMinutes) => {
    if (!lunchTime) return false;

    const timeToMinutes = (timeStr) => {
      if (!timeStr) return 0;
      const [h, m] = timeStr.split(':').map(Number);
      return h * 60 + m;
    };

    const startMins = timeToMinutes(dateToTimeString(lunchTime));
    const endMins = startMins + durationMinutes;
    if (endMins > 24 * 60) return false; // a meal break can't spill past midnight

    const ranges = formData.blocks
      .filter((block) => block.start_time && block.end_time)
      .map((block) => [timeToMinutes(block.start_time), timeToMinutes(block.end_time)]);

    const contained = ranges.some(([s, e]) => s <= startMins && e >= endMins && e - s > durationMinutes);
    const overlapsAny = ranges.some(([s, e]) => startMins < e && endMins > s);
    return contained || !overlapsAny;
  }, [lunchTime, formData.blocks]);

  // Handle activity type change for a block
  const handleBlockActivityTypeChange = (index, newType) => {
    if (newType !== null) {
      setFormData((prev) => ({
        ...prev,
        blocks: prev.blocks.map((block, i) =>
          i === index
            ? {
                ...block,
                activity_type: newType,
                maintenance: newType === 'maintenance' ? block.maintenance : null,
                other_description: newType === 'other' ? block.other_description : '',
              }
            : block
        ),
      }));
    }
  };

  // Handle save
  const handleSave = async () => {
    setError(null);

    // Validation
    if (formData.blocks.length === 0) {
      setError(t('At least one time block is required'));
      return;
    }

    // NB: no description requirement for "Muu työ" blocks — the time clock's gap-fill creates
    // them with an empty description by design, and the backend accepts that. Requiring one
    // here made clock-tracked days impossible to save through this dialog.
    for (let i = 0; i < formData.blocks.length; i++) {
      const block = formData.blocks[i];
      if (block.activity_type === 'maintenance' && !block.maintenance) {
        setError(t('Please select a maintenance for block') + ` ${i + 1}`);
        return;
      }
    }

    if (isManagerRole && !formData.employee) {
      setError(t('Please select an employee'));
      return;
    }

    setSaving(true);

    try {
      const payload = {
        employee: formData.employee,
        work_date: formData.work_date,
        blocks: formData.blocks.map((block, index) => ({
          id: block.id,
          start_time: block.start_time,
          end_time: block.end_time,
          activity_type: block.activity_type,
          maintenance: block.activity_type === 'maintenance' ? block.maintenance : null,
          other_description: block.activity_type === 'other' ? block.other_description : '',
          notes: block.notes,
          order: index,
        })),
      };

      let response;
      // Check if we're updating an existing work day
      if (isEditing) {
        response = await axios.put(`/api/scheduling/work-days/${workDay.id}/`, payload);
      } else {
        // Check if work day exists for this date/employee
        const checkResponse = await axios.get('/api/scheduling/work-days/by_date/', {
          params: { date: formData.work_date, employee: formData.employee },
        });

        if (checkResponse.data.exists) {
          // Update existing
          const existingId = checkResponse.data.work_day.id;
          response = await axios.put(`/api/scheduling/work-days/${existingId}/`, payload);
        } else {
          // Create new
          response = await axios.post('/api/scheduling/work-days/', payload);
        }
      }

      onSaved(response.data);
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData) {
        if (typeof errorData === 'string') {
          setError(errorData);
        } else if (errorData.blocks) {
          setError(t('Error in time blocks: ') + JSON.stringify(errorData.blocks));
        } else {
          const messages = Object.values(errorData).flat();
          setError(messages.join(' '));
        }
      } else {
        setError(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const totalHours = calculateTotalHours(formData.blocks);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
          {isEditing ? t('Edit Work Day') : t('Add Work Day')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert ref={errorRef} severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {overlapWarnings.length > 0 && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 2, borderRadius: '8px' }}
          >
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('Overlapping time blocks detected')}
            </Typography>
            {overlapWarnings.map((warning, idx) => (
              <Typography key={idx} sx={{ fontSize: '13px' }}>
                {warning}
              </Typography>
            ))}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Employee selector (for managers) */}
          {isManagerRole && employees.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>{t('Employee')} *</InputLabel>
              <Select
                value={formData.employee}
                onChange={(e) => handleEmployeeChange(e.target.value)}
                label={t('Employee') + ' *'}
                disabled={isEditing}
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Date */}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DatePicker
              label={t('Date')}
              value={dateStringToDateObj(formData.work_date)}
              onChange={(newValue) => handleDateChange(dateObjToDateString(newValue))}
              format="dd.MM.yyyy"
              disabled={isEditing}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>

          <Divider sx={{ my: 1 }} />

          {/* Time Blocks Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('Time Blocks')}
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addBlock}
              size="small"
              variant="outlined"
            >
              {t('Add Block')}
            </Button>
          </Box>

          {/* Lunch Break Section */}
          {formData.blocks.length > 0 && (
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: 'orange.50', borderColor: 'orange.200' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <RestaurantIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                  {t('Lunch break')}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 1.5 }}>
                {t(
                  'Adds a meal break at the set time. If it lands inside a work period, that period is split around it.',
                )}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <TimePicker
                    value={lunchTime}
                    onChange={(newValue) => setLunchTime(newValue)}
                    ampm={false}
                    slotProps={{
                      textField: { size: 'small', sx: { width: 140 } },
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleLunchBreak(30)}
                    disabled={!canInsertLunchBreak(30)}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    30 min
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleLunchBreak(60)}
                    disabled={!canInsertLunchBreak(60)}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    60 min
                  </Button>
                </Box>
              </LocalizationProvider>
            </Paper>
          )}

          {formData.blocks.length === 0 && (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: 'center',
                bgcolor: 'grey.50',
                borderStyle: 'dashed',
              }}
            >
              <Typography color="text.secondary">
                {t('No time blocks yet. Click "Add Block" to add one.')}
              </Typography>
            </Paper>
          )}

          {formData.blocks.map((block, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{ p: 2, bgcolor: 'background.default' }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                  {t('Block')} {index + 1}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeBlock(index)}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Time inputs */}
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TimePicker
                      label={t('Start Time')}
                      value={timeStringToDate(block.start_time)}
                      onChange={(newValue) => updateBlock(index, 'start_time', dateToTimeString(newValue))}
                      ampm={false}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true },
                      }}
                    />
                    <TimePicker
                      label={t('End Time')}
                      value={timeStringToDate(block.end_time)}
                      onChange={(newValue) => updateBlock(index, 'end_time', dateToTimeString(newValue))}
                      ampm={false}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true },
                      }}
                    />
                  </Box>
                </LocalizationProvider>

                {/* Reversed times count as an overnight shift (+24h) — must never hide. */}
                {block.start_time && block.end_time && block.end_time < block.start_time && (
                  <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ py: 0 }}>
                    {t('worktime.overnightBlockWarning')}
                  </Alert>
                )}

                {/* Activity Type Toggle */}
                <ToggleButtonGroup
                  value={block.activity_type}
                  exclusive
                  onChange={(e, val) => handleBlockActivityTypeChange(index, val)}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      // Shrink/stack on mobile so the 3 buttons never overflow a narrow screen:
                      // icon on top of the label on xs, side-by-side from sm up.
                      minWidth: 0,
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 0.25, sm: 0 },
                      py: 1,
                      px: { xs: 0.5, sm: 1 },
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                      lineHeight: 1.2,
                      whiteSpace: 'normal',
                      '&.Mui-selected': {
                        bgcolor: 'secondary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'secondary.light',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="maintenance">
                    <BuildIcon sx={{ mr: { xs: 0, sm: 1 }, fontSize: 16, flexShrink: 0 }} />
                    {t('Maintenance')}
                  </ToggleButton>
                  <ToggleButton value="other">
                    <DescriptionIcon sx={{ mr: { xs: 0, sm: 1 }, fontSize: 16, flexShrink: 0 }} />
                    {t('worktime.reconcile.otherWork')}
                  </ToggleButton>
                  <ToggleButton value="break">
                    <RestaurantIcon sx={{ mr: { xs: 0, sm: 1 }, fontSize: 16, flexShrink: 0 }} />
                    {t('worktime.reconcile.break')}
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Maintenance selector */}
                {block.activity_type === 'maintenance' && (
                  <MaintenanceBlockSelect
                    t={t}
                    block={block}
                    onChange={(id) => updateBlock(index, 'maintenance', id)}
                  />
                )}

                {/* Other description (optional — clock gap-fill blocks have none) */}
                {block.activity_type === 'other' && (
                  <TextField
                    label={t('Description')}
                    value={block.other_description}
                    onChange={(e) => updateBlock(index, 'other_description', e.target.value)}
                    fullWidth
                    placeholder={t('e.g., Cleaning, Training, Meeting')}
                    size="small"
                  />
                )}

                {/* Notes (optional) */}
                <TextField
                  label={t('Notes')}
                  value={block.notes}
                  onChange={(e) => updateBlock(index, 'notes', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder={t('Optional notes')}
                />
              </Box>
            </Paper>
          ))}

          {/* Total Hours */}
          {formData.blocks.length > 0 && (
            <Paper
              sx={{
                p: 2,
                bgcolor: 'primary.50',
                border: 1,
                borderColor: 'primary.200',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon sx={{ color: 'primary.main' }} />
                <Typography data-testid="workday-dialog-total" sx={{ fontWeight: 600 }}>
                  {t('Total Hours')}: {formatQuantity(totalHours)} h
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || formData.blocks.length === 0}
          sx={{
            bgcolor: 'secondary.main',
            '&:hover': { bgcolor: 'secondary.light' },
          }}
        >
          {saving ? <CircularProgress size={24} /> : <span>{t('Save')}</span>}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withTranslation()(WorkDayDialog);
